import { useState } from "react";
import {
  DocumentTypeSource,
  SourceDocumentDefinition,
} from "../types/documentTypes";
import { useAuthFetch } from "../../../utils/useAuthFetch";

export const useDocumentSources = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const createSource = async (
    documentTypeId: number,
    source: SourceDocumentDefinition,
    workspace_id: string,
  ): Promise<DocumentTypeSource> => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/sources?workspace_id=${workspace_id}`,
        "create source",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...source,
            DocumentTypeID: documentTypeId,
          }),
        },
      );

      return response;
    } catch (err: any) {
      setError(err.message || "Failed to create source field");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteSource = async (
    documentTypeId: number,
    sourceFieldId: number,
    workspace_id: string,
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/sources/${sourceFieldId}?workspace_id=${workspace_id}`,
        "delete source",
        {
          method: "DELETE",
        },
      );
    } catch (err: any) {
      setError(err.message || "Failed to delete source field");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateSource = async (
    documentTypeId: number,
    sourceFieldId: number,
    updates: Partial<DocumentTypeSource>,
    workspace_id: string,
  ): Promise<DocumentTypeSource> => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/sources/${sourceFieldId}?workspace_id=${workspace_id}`,
        "update source",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updates),
        },
      );

      return response;
    } catch (err: any) {
      setError(err.message || "Failed to update source field");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getSources = async (
    documentTypeId: number,
    workspace_id: string,
  ): Promise<DocumentTypeSource[]> => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/sources?workspace_id=${workspace_id}`,
        "fetch sources",
      );

      return response;
    } catch (err: any) {
      setError(err.message || "Failed to fetch source fields");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    createSource,
    deleteSource,
    updateSource,
    getSources,
    loading,
    error,
  };
};
